import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  EnvelopeIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  TrashIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
  CheckIcon,
  ClockIcon,
  TagIcon,
  UserIcon,
  PhoneIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline'
import { inquiryAPI } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'

interface Inquiry {
  id: string
  reference: string
  name: string
  email: string
  phone?: string
  subject: string
  message: string
  status: 'new' | 'in_progress' | 'resolved' | 'closed'
  priority: 'low' | 'medium' | 'high' | 'urgent'
  source: string
  assignedTo?: {
    _id: string
    name: string
    email: string
  }
  response?: string
  responseDate?: string
  tags: string[]
  createdAt: string
  updatedAt: string
  metadata?: {
    referrer?: string
    deviceType?: string
    location?: string
  }
}

interface InquiryStats {
  total: number
  recent24h: number
  averageResponseTimeHours: number | null
  byStatus: {
    new?: number
    in_progress?: number
    resolved?: number
    closed?: number
  }
  byPriority: {
    low?: number
    medium?: number
    high?: number
    urgent?: number
  }
}

const Inquiries: React.FC = () => {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [priority, setPriority] = useState('')
  const [selectedInquiries, setSelectedInquiries] = useState<string[]>([])
  const queryClient = useQueryClient()

  // Fetch inquiries
  const { data: inquiriesData, isLoading, error } = useQuery({
    queryKey: ['inquiries', page, search, status, priority],
    queryFn: () => inquiryAPI.getAll({
      page,
      limit: 20,
      search,
      status,
      priority
    }).then(res => res.data),
    refetchInterval: 5000, // Refetch every 5 seconds
  })

  // Fetch statistics
  const { data: statsData } = useQuery<InquiryStats>({
    queryKey: ['inquiries-stats'],
    queryFn: () => inquiryAPI.getStats().then(res => res.data),
    refetchInterval: 10000,
  })

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      inquiryAPI.update(id, data),
    onSuccess: () => {
      toast.success('Inquiry updated successfully')
      queryClient.invalidateQueries({ queryKey: ['inquiries'] })
      queryClient.invalidateQueries({ queryKey: ['inquiries-stats'] })
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to update inquiry')
    }
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => inquiryAPI.delete(id),
    onSuccess: () => {
      toast.success('Inquiry deleted successfully')
      queryClient.invalidateQueries({ queryKey: ['inquiries'] })
      queryClient.invalidateQueries({ queryKey: ['inquiries-stats'] })
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to delete inquiry')
    }
  })

  // Bulk update mutation
  const bulkUpdateMutation = useMutation({
    mutationFn: ({ ids, updates }: { ids: string[]; updates: any }) =>
      inquiryAPI.bulkUpdate(ids, updates),
    onSuccess: (data) => {
      toast.success(data.data.message)
      queryClient.invalidateQueries({ queryKey: ['inquiries'] })
      queryClient.invalidateQueries({ queryKey: ['inquiries-stats'] })
      setSelectedInquiries([])
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Bulk update failed')
    }
  })

  const handleSelectAll = () => {
    if (selectedInquiries.length === inquiriesData?.inquiries?.length) {
      setSelectedInquiries([])
    } else {
      setSelectedInquiries(inquiriesData?.inquiries?.map((i: Inquiry) => i.id) || [])
    }
  }

  const handleSelectInquiry = (id: string) => {
    setSelectedInquiries(prev =>
      prev.includes(id) ? prev.filter(iId => iId !== id) : [...prev, id]
    )
  }

  const handleExport = async () => {
    try {
      const response = await inquiryAPI.exportCSV({ status, priority })
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `inquiries-export-${new Date().toISOString()}.csv`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      toast.success('Inquiries exported successfully')
    } catch (error) {
      toast.error('Failed to export inquiries')
    }
  }

  const handleStatusChange = (id: string, newStatus: string) => {
    updateMutation.mutate({ id, data: { status: newStatus } })
  }

  const handlePriorityChange = (id: string, newPriority: string) => {
    updateMutation.mutate({ id, data: { priority: newPriority } })
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new': return 'bg-primary-100 text-primary-800'
      case 'in_progress': return 'bg-yellow-100 text-yellow-800'
      case 'resolved': return 'bg-green-100 text-green-800'
      case 'closed': return 'bg-muted text-foreground'
      default: return 'bg-muted text-foreground'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'low': return 'bg-muted text-foreground'
      case 'medium': return 'bg-primary-100 text-primary-800'
      case 'high': return 'bg-orange-100 text-orange-800'
      case 'urgent': return 'bg-red-100 text-red-800'
      default: return 'bg-muted text-foreground'
    }
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-center text-red-600">
          Error loading inquiries: {(error as any).response?.data?.error || 'Unknown error'}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <EnvelopeIcon className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-foreground">Inquiries</h1>
            <p className="text-sm text-muted-foreground">
              {inquiriesData?.pagination?.total || 0} total inquiries
            </p>
          </div>
        </div>
        <button
          onClick={handleExport}
          className="btn-secondary flex items-center space-x-2"
        >
          <ArrowDownTrayIcon className="h-4 w-4" />
          <span>Export CSV</span>
        </button>
      </div>

      {/* Statistics */}
      {statsData && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Inquiries</p>
                <p className="text-2xl font-bold text-foreground">{statsData.total}</p>
              </div>
              <EnvelopeIcon className="h-8 w-8 text-muted-foreground/70" />
            </div>
          </div>
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Last 24 Hours</p>
                <p className="text-2xl font-bold text-primary">{statsData.recent24h}</p>
              </div>
              <ClockIcon className="h-8 w-8 text-primary-400" />
            </div>
          </div>
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">New</p>
                <p className="text-2xl font-bold text-primary">{statsData.byStatus.new || 0}</p>
              </div>
              <div className="text-xs text-muted-foreground space-y-1">
                <div>In Progress: {statsData.byStatus.in_progress || 0}</div>
                <div>Resolved: {statsData.byStatus.resolved || 0}</div>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg Response Time</p>
                <p className="text-2xl font-bold text-green-600">
                  {statsData.averageResponseTimeHours ? `${statsData.averageResponseTimeHours}h` : 'N/A'}
                </p>
              </div>
              <CheckIcon className="h-8 w-8 text-green-400" />
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="card">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground/70" />
              <input
                type="text"
                placeholder="Search inquiries..."
                className="pl-10 input-field"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value)
                  setPage(1)
                }}
              />
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <FunnelIcon className="h-4 w-4 text-muted-foreground/70" />
              <select
                className="input-field w-auto min-w-[9.5rem]"
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value)
                  setPage(1)
                }}
              >
                <option value="">All Status</option>
                <option value="new">New</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            <select
              className="input-field w-auto min-w-[9.5rem]"
              value={priority}
              onChange={(e) => {
                setPriority(e.target.value)
                setPage(1)
              }}
            >
              <option value="">All Priority</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedInquiries.length > 0 && (
        <div className="card">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              {selectedInquiries.length} selected
            </span>
            <div className="flex space-x-2">
              <select
                className="input-field text-sm"
                onChange={(e) => {
                  if (e.target.value) {
                    bulkUpdateMutation.mutate({
                      ids: selectedInquiries,
                      updates: { status: e.target.value }
                    })
                    e.target.value = ''
                  }
                }}
                defaultValue=""
              >
                <option value="">Change Status</option>
                <option value="new">New</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
              <select
                className="input-field text-sm"
                onChange={(e) => {
                  if (e.target.value) {
                    bulkUpdateMutation.mutate({
                      ids: selectedInquiries,
                      updates: { priority: e.target.value }
                    })
                    e.target.value = ''
                  }
                }}
                defaultValue=""
              >
                <option value="">Change Priority</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
              <button
                onClick={() => {
                  if (confirm('Are you sure you want to delete selected inquiries?')) {
                    selectedInquiries.forEach(id => deleteMutation.mutate(id))
                    setSelectedInquiries([])
                  }
                }}
                className="btn-sm btn-danger"
                disabled={deleteMutation.isPending}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Inquiries Table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner size="lg" />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-border">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      <input
                        type="checkbox"
                        checked={selectedInquiries.length === inquiriesData?.inquiries?.length && inquiriesData?.inquiries?.length > 0}
                        onChange={handleSelectAll}
                        className="rounded border-input"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Reference
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Subject
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Priority
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-card divide-y divide-border">
                  {inquiriesData?.inquiries?.map((inquiry: Inquiry) => (
                    <tr key={inquiry.id} className="hover:bg-muted/50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedInquiries.includes(inquiry.id)}
                          onChange={() => handleSelectInquiry(inquiry.id)}
                          className="rounded border-input"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-mono font-medium text-foreground">
                          {inquiry.reference}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-start space-x-2">
                          <UserIcon className="h-4 w-4 text-muted-foreground/70 mt-1" />
                          <div className="min-w-0">
                            {/* Same reasoning as the conversations list: the
                                eye icon is a small target for the row's main
                                action, so the name opens it too. */}
                            <Link
                              to={`/inquiries/${inquiry.id}`}
                              className="block text-sm font-medium text-foreground underline-offset-4 hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
                            >
                              {inquiry.name}
                            </Link>
                            <div className="text-xs text-muted-foreground truncate">{inquiry.email}</div>
                            {inquiry.phone && (
                              <div className="text-xs text-muted-foreground flex items-center space-x-1">
                                <PhoneIcon className="h-3 w-3" />
                                <span>{inquiry.phone}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-foreground font-medium max-w-xs truncate">
                          {inquiry.subject}
                        </div>
                        <div className="text-xs text-muted-foreground max-w-xs truncate">
                          {inquiry.message}
                        </div>
                        {inquiry.tags.length > 0 && (
                          <div className="flex items-center space-x-1 mt-1">
                            <TagIcon className="h-3 w-3 text-muted-foreground/70" />
                            <span className="text-xs text-muted-foreground">
                              {inquiry.tags.join(', ')}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          value={inquiry.priority}
                          onChange={(e) => handlePriorityChange(inquiry.id, e.target.value)}
                          className={`text-xs px-2 py-1 rounded-full border-0 font-medium ${getPriorityColor(inquiry.priority)}`}
                          disabled={updateMutation.isPending}
                        >
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                          <option value="urgent">Urgent</option>
                        </select>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          value={inquiry.status}
                          onChange={(e) => handleStatusChange(inquiry.id, e.target.value)}
                          className={`text-xs px-2 py-1 rounded-full border-0 font-medium ${getStatusColor(inquiry.status)}`}
                          disabled={updateMutation.isPending}
                        >
                          <option value="new">New</option>
                          <option value="in_progress">In Progress</option>
                          <option value="resolved">Resolved</option>
                          <option value="closed">Closed</option>
                        </select>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                          <CalendarIcon className="h-3 w-3" />
                          <span>{new Date(inquiry.createdAt).toLocaleDateString()}</span>
                        </div>
                        <div className="text-xs text-muted-foreground/70">
                          {new Date(inquiry.createdAt).toLocaleTimeString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex space-x-2">
                          <Link
                            to={`/inquiries/${inquiry.id}`}
                            className="text-primary hover:text-primary"
                            title="View details"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </Link>
                          <button
                            onClick={() => {
                              if (confirm('Are you sure you want to delete this inquiry?')) {
                                deleteMutation.mutate(inquiry.id)
                              }
                            }}
                            className="text-red-600 hover:text-red-900"
                            title="Delete"
                            disabled={deleteMutation.isPending}
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {inquiriesData?.pagination && (
              <div className="bg-card px-4 py-3 flex items-center justify-between border-t border-border sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-input text-sm font-medium rounded-md text-foreground bg-card hover:bg-muted/50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page >= inquiriesData.pagination.pages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-input text-sm font-medium rounded-md text-foreground bg-card hover:bg-muted/50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-foreground">
                      Showing{' '}
                      <span className="font-medium">
                        {((inquiriesData.pagination.page - 1) * inquiriesData.pagination.limit) + 1}
                      </span>{' '}
                      to{' '}
                      <span className="font-medium">
                        {Math.min(inquiriesData.pagination.page * inquiriesData.pagination.limit, inquiriesData.pagination.total)}
                      </span>{' '}
                      of{' '}
                      <span className="font-medium">{inquiriesData.pagination.total}</span>{' '}
                      results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => setPage(page - 1)}
                        disabled={page === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-input bg-card text-sm font-medium text-muted-foreground hover:bg-muted/50 disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setPage(page + 1)}
                        disabled={page >= inquiriesData.pagination.pages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-input bg-card text-sm font-medium text-muted-foreground hover:bg-muted/50 disabled:opacity-50"
                      >
                        Next
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* View Inquiry Modal */}
    </div>
  )
}

export default Inquiries
